import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/ContentPage";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { SakerhetsfilmShowcase } from "@/components/sections/SakerhetsfilmShowcase";

import { IMAGES, BBN_PHOTOS, BBN_SECURITY } from "@/lib/images";

export const FAQS: FaqItem[] = [
  {
    q: "Vilken skyddsklass enligt EN 12600 behöver en butik?",
    a: "För skyltfönster i gatuplan rekommenderar vi minst klass 2B2 (motsvarar laminerat säkerhetsglas) — det stoppar normalt en spontanattack med tegelsten, kofot eller hammare under tillräcklig tid för att utlösa larmrespons. För juvelerare, apotek och butiker med högvärdesvaror går vi upp till 1B1, som är högsta personsäkerhetsklass och tål upprepade slag.",
  },
  {
    q: "Stoppar filmen ett krossförsök helt?",
    a: "Nej, glaset kan fortfarande krossas. Säkerhetsfilmen håller ihop skärvorna så att det tar längre tid att komma igenom rutan. I praktiken handlar det om att fördröja inbrottet 30–90 sekunder, vilket är mer än tillräckligt för att larmcentralen ska hinna larma ut och inkräktaren oftast avbryter försöket.",
  },
  {
    q: "Vad säger försäkringsbolagen om säkerhetsfilm?",
    a: "Större försäkringsbolag (Trygg-Hansa, If, Länsförsäkringar) accepterar säkerhetsfilm enligt EN 12600 som en del av ett komplett inbrottsskydd och kan ge premiereduktion vid kombination med larm och kameror. För butiker som hanterar högvärdesvaror är film ofta ett krav i försäkringsvillkoret tillsammans med rullgaller eller larm.",
  },
  {
    q: "Kan ni installera under bibehållen butikstidsdrift?",
    a: "Ja. Installation av säkerhetsfilm på en standardbutik tar normalt 1–2 dagar och planeras så att butiken kan hålla öppet så mycket som möjligt. Arbetet är dammfritt och butiken kan öppna som vanligt direkt efter. Filmen är fullt funktionell efter 1 timmas härdtid (full optisk klarhet inom 30 dagar).",
  },
  {
    q: "Kan säkerhetsfilm kombineras med solfilm och spegelfilm?",
    a: "Ja, det är vanligt i butiker. 3M Ultra Night Vision kombinerar inbrottsskydd, splitterskydd och solreducering i samma film. För skyltfönster mot söder eller väster ger det både inbrottsfördröjning och 50 % bortreducering av solvärmen, vilket sänker kylkostnader och skyddar varor i skyltfönstret från UV-blekning.",
  },
  {
    q: "Vad gäller efter ett inbrottsförsök — håller filmen?",
    a: "Ja. Efter ett krossförsök sitter filmen kvar och håller ihop glaset tills ni hinner byta rutan. Glaset ska bytas snarast (det har förlorat sin bärande funktion), men filmen ger er tid att stänga, säkra varor och boka glasmästare utan att butiken står öppen mot gatan.",
  },
];

export const Route = createFileRoute("/sakerhetsfilm-butik")({
  head: () => ({
    meta: [
      { title: "Säkerhetsfilm för butik & detaljhandel | EN 12600 | BBN Nordic" },
      { name: "description", content: "Säkerhetsfilm för butiker — inbrottsfördröjning enligt EN 12600 klass 2B2 och 1B1. Skyddar skyltfönster, högvärdesvaror och personal. Installation utan stängd butik." },
      { property: "og:title", content: "Säkerhetsfilm för butik" },
      { property: "og:description", content: "Inbrottsfördröjning för skyltfönster och butiksglas enligt EN 12600 — installation utan att stänga butiken." },
      { property: "og:url", content: "https://bbnnordic.se/sakerhetsfilm-butik" },
      { property: "og:image", content: BBN_PHOTOS.shopSecurity },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/sakerhetsfilm-butik" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Säkerhetsfilm för butik och detaljhandel",
          serviceType: "Inbrottsskyddande fönsterfilm enligt EN 12600",
          description: "Säkerhetsfilm för butiker, skyltfönster och högvärdesförvaring — inbrottsfördröjning och splitterskydd enligt EN 12600.",
          provider: { "@type": "LocalBusiness", "@id": "/#organization", name: "BBN Nordic" },
          areaServed: "SE",
        }),
      },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(FAQS)) },
    ],
  }),
  component: () => (
    <ContentPage
      disableCta
      eyebrow="BRANSCH — BUTIK"
      title="Säkerhetsfilm för butik"
      subtitle="Inbrottsfördröjning enligt EN 12600, splitterskydd vid vandalism och UV-skydd för exponerade varor utan att byta glas eller stänga butiken."
      heroImage={BBN_PHOTOS.shopSecurity}
      breadcrumbs={[{ label: "Säkerhetsfilm butik" }]}
      sections={[
        {
          eyebrow: "REGELVERK",
          heading: "EN 12600 — hur skyddsklasserna fungerar i praktiken.",
          paragraphs: [
            "EN 12600 är den europeiska standarden för pendelslagstest på säkerhetsglas och säkerhetsfilm. Klassningen anges med tre tecken: höjd för pendelfallet, brottsmönster och högsta klass där filmen håller ihop glaset. Lägre siffror = högre säkerhet.",
            "I butiksmiljö är klass 2B2 standard för skyltfönster (motsvarar laminerat säkerhetsglas) och klass 1B1 för apotek, juvelerare, klockbutiker och tobakshandel. Vi rekommenderar alltid 2B2 som lägstanivå mot gatufönster oavsett bransch — kostnadsskillnaden mellan ren splitterfilm och 2B2-certifierad film är liten.",
          ],
          bullets: [
            "3B3 — splitterskydd, motsvarar härdat glas (minimum)",
            "2B2 — inbrottsfördröjning, motsvarar laminerat säkerhetsglas",
            "1B1 — högsta personsäkerhetsklass, tål upprepade slag",
            "Klassning enligt EN 12600 är dokumenterad i 3M:s produktblad",
            "Försäkringsvillkor refererar oftast till EN 12600-klass",
          ],
          image: BBN_PHOTOS.installation2,
        },
        {
          eyebrow: "VARFÖR DET FUNGERAR",
          heading: "Filmen håller ihop glaset och fördröjer intrång.",
          paragraphs: [
            "Enligt polisens statistik avbryts 80 % av butiksinbrotten om det tar mer än 60 sekunder att komma in. Säkerhetsfilm håller ihop glaset efter att det krossats. I stället för att kliva in på 5 sekunder behöver inkräktaren arbeta i 30–90 sekunder för att få upp en öppning.",
            "Under den tiden hinner inbrottslarmet aktiveras, väktarbil larmas ut och inkräktaren oftast lämnar platsen. Filmen används tillsammans med larm, rullgaller och kameror, inte i stället för dem. Den ger ofta mest skydd per krona.",
          ],
          bullets: [
            "30–90 sekunders fördröjning på spontana inbrottsförsök",
            "Förhindrar smash-and-grab — typiska butiksrån",
            "Skyddar mot vandalism och stenkastning",
            "Personskadeskydd för personal vid explosion eller stöt",
            "Splitterhållande efter sprängladdning vid bankomatrelaterade brott",
          ],
          image: BBN_PHOTOS.burglaryProtection,
        },
        {
          eyebrow: "INSTALLATION",
          heading: "Så monterar vi säkerhetsfilm i din butik.",
          paragraphs: [
            "Vi planerar monteringen tillsammans med er så att butiken påverkas så lite som möjligt. En standardbutik med 4–8 skyltfönster (totalt 30–60 m² glas) tar 1–2 dagar att slutföra. Arbetet är dammfritt och butiken kan öppna direkt efter. Full optisk klarhet kommer inom 30 dagar.",
          ],
          bullets: [
            "1–2 dagars installation per butik",
            "Montering planerad tillsammans med butiken",
            "Dammfritt arbete — ingen påverkan på varuexposition",
            "Direkt funktionell efter 1 timme",
            "Dokumentation till försäkringsbolaget efter färdig installation",
          ],
          image: BBN_SECURITY[1],
        },
      ]}
      related={[
        { to: "/sakerhetsfilm", label: "Säkerhetsfilm — översikt", desc: "Hela produktsortimentet och tekniken." },
        { to: "/sakerhetsfilm-skyddsklass-en12600-guide", label: "EN 12600-guide", desc: "Välj rätt skyddsklass." },
        { to: "/splitterskydd-fonster", label: "Splitterskydd", desc: "Grundläggande splitterskydd för glas." },
        { to: "/offert", label: "Begär offert", desc: "Underlag för butiksägare och butikskedjor." },
      ]}
      priorityContent={<SakerhetsfilmShowcase />}
    >
      <FaqSection heading="Vanliga frågor från butiksägare och butikschefer." items={FAQS} />
    </ContentPage>
  ),
});
